import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import MonthPicker from '../common/MonthPicker';
import TableSkeleton from '../common/TableSkeleton';
import eingangsbelegeService, {
  Monatsuebersicht,
} from '../../services/eingangsbelegeService';

interface Props {
  refreshKey: number;
}

const currentMonth = () => new Date().toISOString().slice(0, 7);

const labels: Record<string, string> = {
  vorhanden: 'Rechnung vorhanden',
  geprueft_ohne_beleg: 'Geprüft, keine Rechnung',
  ausstehend: 'Noch im Zeitfenster',
  fehlend: 'Abruf fehlt',
  login_noetig: 'Login erforderlich',
  fehler: 'Connector-Fehler',
  nicht_erwartet: 'Nicht monatlich',
};

const colors: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  vorhanden: 'success',
  geprueft_ohne_beleg: 'info',
  ausstehend: 'default',
  fehlend: 'warning',
  login_noetig: 'warning',
  fehler: 'error',
  nicht_erwartet: 'default',
};

const MonatsuebersichtTab: React.FC<Props> = ({ refreshKey }) => {
  const [month, setMonth] = useState(currentMonth());
  const [overview, setOverview] = useState<Monatsuebersicht | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setOverview(await eingangsbelegeService.monthly(month));
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) return <TableSkeleton rows={6} columns={6} />;
  if (!overview) return null;

  const cards = [
    { label: 'Erwartete Konten', value: overview.erwartet, color: 'text.primary' },
    { label: 'Mit Beleg', value: overview.vorhanden, color: 'success.main' },
    { label: 'Abruf fehlt', value: overview.fehlend, color: 'warning.main' },
    { label: 'Login / Fehler', value: overview.login_noetig + overview.fehler, color: 'error.main' },
    { label: 'Neu / Klärung', value: overview.neu + overview.klaerung, color: 'info.main' },
    { label: 'Verbucht', value: overview.verbucht, color: 'success.main' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6">Monatsvollständigkeit</Typography>
          <Typography variant="body2" color="text.secondary">
            Zeigt, ob jedes erwartete Anbieter-Konto geprüft wurde und Rechnungen bereitliegen.
          </Typography>
        </Box>
        <MonthPicker
          label="Abrechnungsmonat"
          value={month}
          testId="abrechnungsmonat-filter"
          onChange={setMonth}
        />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card) => (
          <Grid item xs={6} md={4} lg={2} key={card.label}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="h5" sx={{ color: card.color }}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Anbieter-Konto</TableCell>
              <TableCell>Eigentümer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Belege</TableCell>
              <TableCell>Letzter Abruf</TableCell>
              <TableCell>Hinweis</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {overview.konten.map((account) => (
              <TableRow key={account.anbieter_konto_id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{account.bezeichnung}</Typography>
                  <Typography variant="caption" color="text.secondary">{account.anbieter_code}</Typography>
                </TableCell>
                <TableCell>
                  {account.owner ? `${account.owner.vorname} ${account.owner.nachname}` : 'Nicht zugeordnet'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={labels[account.status] || account.status}
                    color={colors[account.status] || 'default'}
                  />
                </TableCell>
                <TableCell align="center">{account.belege_anzahl}</TableCell>
                <TableCell>
                  {account.letzter_sync_am
                    ? new Date(account.letzter_sync_am).toLocaleString('de-DE')
                    : '–'}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color={account.status === 'fehler' ? 'error' : 'text.secondary'}>
                    {account.letzter_fehler_hinweis || '–'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {overview.konten.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  Keine aktiven Anbieter-Konten in diesem Monat.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MonatsuebersichtTab;
